import { apiClient } from './apiClient';
import { roleOverrideServiceMock } from './roleOverrideService.mock';
import type {
  ProjectRoleOverride,
  EmployeeRoleOverride,
  CreateProjectRoleOverrideDto,
  CreateEmployeeRoleOverrideDto,
  UpdateProjectRoleOverrideDto,
  UpdateEmployeeRoleOverrideDto,
  RateResolutionRequest,
  RateResolutionResponse,
} from '@/types/roleOverride';

// Use mock service for now (switch to real API when backend is ready)
const USE_MOCK = true;

const realRoleOverrideService = {
  // Project Role Override operations
  async getProjectRoleOverrides(projectId?: number): Promise<ProjectRoleOverride[]> {
    const url = projectId
      ? `/role-overrides/project?projectId=${projectId}`
      : `/role-overrides/project`;
    return await apiClient.get<ProjectRoleOverride[]>(url);
  },

  async getProjectRoleOverrideById(id: number): Promise<ProjectRoleOverride> {
    return await apiClient.get<ProjectRoleOverride>(`/role-overrides/project/${id}`);
  },

  async createProjectRoleOverride(dto: CreateProjectRoleOverrideDto): Promise<ProjectRoleOverride> {
    return await apiClient.post<ProjectRoleOverride>(`/role-overrides/project`, dto);
  },

  async updateProjectRoleOverride(
    id: number,
    dto: UpdateProjectRoleOverrideDto
  ): Promise<ProjectRoleOverride> {
    return await apiClient.put<ProjectRoleOverride>(`/role-overrides/project/${id}`, dto);
  },

  async deleteProjectRoleOverride(id: number): Promise<void> {
    await apiClient.delete(`/role-overrides/project/${id}`);
  },

  // Employee Role Override operations
  async getEmployeeRoleOverrides(employeeId?: number, projectId?: number): Promise<EmployeeRoleOverride[]> {
    const params = new URLSearchParams();
    if (employeeId) params.append('employeeId', employeeId.toString());
    if (projectId !== undefined) params.append('projectId', projectId.toString());

    const url = `/role-overrides/employee${params.toString() ? `?${params.toString()}` : ''}`;
    return await apiClient.get<EmployeeRoleOverride[]>(url);
  },

  async getEmployeeRoleOverrideById(id: number): Promise<EmployeeRoleOverride> {
    return await apiClient.get<EmployeeRoleOverride>(`/role-overrides/employee/${id}`);
  },

  async createEmployeeRoleOverride(dto: CreateEmployeeRoleOverrideDto): Promise<EmployeeRoleOverride> {
    return await apiClient.post<EmployeeRoleOverride>(`/role-overrides/employee`, dto);
  },

  async updateEmployeeRoleOverride(
    id: number,
    dto: UpdateEmployeeRoleOverrideDto
  ): Promise<EmployeeRoleOverride> {
    return await apiClient.put<EmployeeRoleOverride>(`/role-overrides/employee/${id}`, dto);
  },

  async deleteEmployeeRoleOverride(id: number): Promise<void> {
    await apiClient.delete(`/role-overrides/employee/${id}`);
  },

  // Rate Resolution
  async resolveRate(request: RateResolutionRequest): Promise<RateResolutionResponse> {
    return await apiClient.post<RateResolutionResponse>(`/role-overrides/resolve-rate`, request);
  },
};

export const roleOverrideService = USE_MOCK ? roleOverrideServiceMock : realRoleOverrideService;
